import {
  ChartNoAxesColumnIncreasing,
  TriangleAlert,
  Settings,
  Bell,
  ChevronUp,
  User,
  LogOut,
  CreditCard,
  ScanSearch,
  Plus,
  Bot,
  ArrowLeft,
  Crosshair,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/auth";
import { useState, useEffect } from "react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import type { FeatureFlags } from "@/types/features";
import type { ProductKey } from "@/constants/products";
import { RequestAccessBanner } from "@/components/auth/RequestAccessBanner";

// Menu items grouped by product
const menuItemsByProduct: Record<
  ProductKey,
  Array<{
    title: string;
    url: string;
    icon: React.ComponentType<{ className?: string }>;
    featureFlag: keyof FeatureFlags;
  }>
> = {
  redteam: [
    {
      title: "New Scan",
      url: "/compliance",
      icon: Plus,
      featureFlag: "demo_flow",
    },
    {
      title: "AI Compliance Scan",
      url: "/llm-scans",
      icon: ScanSearch,
      featureFlag: "llm_scans",
    },
  ],
  security: [
    {
      title: "Overview",
      url: "/security",
      icon: ChartNoAxesColumnIncreasing,
      featureFlag: "security_dashboard",
    },
    {
      title: "Agents",
      url: "/agents",
      icon: Bot,
      featureFlag: "security_dashboard",
    },
    {
      title: "Issues",
      url: "/issues",
      icon: TriangleAlert,
      featureFlag: "issues",
    },
    {
      title: "Alerts & Policies",
      url: "/alerts",
      icon: Bell,
      featureFlag: "alerts",
    },
  ],
};

/**
 * Renders the application sidebar with feature-flag filtered navigation and a user menu that provides profile links and logout.
 *
 * The navigation list is organized by product (Red Team, Security) and filtered by feature flags.
 * Only products the user has access to are shown. The user menu displays the current user's name
 * and email and includes links to Profile, Billing, Settings, and a Log out action.
 *
 * @returns The sidebar React element.
 */
export function AppSidebar() {
  const location = useLocation();
  const [navigationView, setNavigationView] = useState<"main" | "redteam">(() =>
    location.pathname === "/compliance" || location.pathname.startsWith("/llm-scans")
      ? "redteam"
      : "main"
  );
  const { user, logout, isLoading } = useAuth();
  const { data: featureFlags, products } = useFeatureFlags();

  const showBanner = featureFlags?.request_access_banner && products.includes("redteam") && !products.includes("security");

  // Get user display name and email from Auth0
  const userName = user?.name || user?.nickname || "User";
  const userEmail = user?.email || "user@example.com";

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin + "/login",
      },
    });
  };

  // Get menu items for a product, filtered by feature flags
  const getProductMenuItems = (productKey: ProductKey) => {
    const items = menuItemsByProduct[productKey];
    if (!featureFlags) {
      return [];
    }
    return items.filter((item) => featureFlags[item.featureFlag] === true);
  };

  const securityItems = products.includes("security") ? getProductMenuItems("security") : [];
  const redTeamItems = products.includes("redteam") ? getProductMenuItems("redteam") : [];
  const isRedTeamRoute =
    location.pathname === "/compliance" || location.pathname.startsWith("/llm-scans");

  return (
    <Sidebar collapsible="icon" className="border-sidebar-border/70">
      <SidebarHeader className="relative h-[68px] justify-center px-4 py-3">
        <SidebarMenu>
          <SidebarMenuItem className="flex items-center justify-between gap-2">
            <SidebarMenuButton size="lg" asChild className="w-auto px-1.5 hover:bg-transparent">
              <Link to="/security" className="flex items-center" data-tour="sidebar-logo">
                <img src="/logo.svg" alt="Vortal Logo" className="vortal-logo size-8 object-contain" />
                <span className="sr-only">Vortal</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarTrigger className="absolute right-3 top-[18px] z-30 size-8 shrink-0 rounded-md border border-sidebar-border/70 text-sidebar-foreground/60 transition-[right] duration-300 ease-out hover:bg-sidebar-accent hover:text-sidebar-foreground motion-reduce:transition-none group-data-[collapsible=icon]:-right-11" />
      </SidebarHeader>
      <SidebarContent className="relative overflow-hidden pt-5">
        <div
          className={`absolute inset-0 overflow-y-auto px-3 transition-transform duration-300 ease-out motion-reduce:transition-none ${
            navigationView === "main" ? "translate-x-0" : "-translate-x-full"
          }`}
          aria-hidden={navigationView !== "main"}
        >
          <SidebarGroup className="p-0">
            <SidebarGroupContent>
              <SidebarMenu data-tour="sidebar-nav">
                {securityItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={
                        location.pathname === item.url ||
                        (item.url !== "/security" && location.pathname.startsWith(`${item.url}/`))
                      }
                      tooltip={item.title}
                      className="h-11 gap-3 rounded-lg px-3 text-[14px] font-normal text-sidebar-foreground/65 hover:bg-background hover:text-sidebar-foreground data-[active=true]:bg-background data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:[&>span]:hidden [&>svg]:size-[18px]"
                    >
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                {redTeamItems.length > 0 && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => setNavigationView("redteam")}
                      isActive={isRedTeamRoute}
                      tooltip="Red Team"
                      className="h-11 gap-3 rounded-lg px-3 text-[14px] font-normal text-sidebar-foreground/65 hover:bg-background hover:text-sidebar-foreground data-[active=true]:bg-background data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:[&>span]:hidden [&>svg]:size-[18px]"
                    >
                      <Crosshair />
                      <span>Red Team</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>

        <div
          className={`absolute inset-0 overflow-y-auto px-3 transition-transform duration-300 ease-out motion-reduce:transition-none ${
            navigationView === "redteam" ? "translate-x-0" : "translate-x-full"
          }`}
          aria-hidden={navigationView !== "redteam"}
        >
          <SidebarGroup className="p-0">
            <SidebarGroupContent>
              <SidebarMenu data-tour="sidebar-nav-redteam">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => setNavigationView("main")}
                    tooltip="Back"
                    className="mb-2 h-11 gap-3 rounded-lg px-3 text-[14px] font-medium text-sidebar-foreground hover:bg-background group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:[&>span]:hidden [&>svg]:size-[18px]"
                  >
                    <ArrowLeft />
                    <span>Back</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {redTeamItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={
                        location.pathname === item.url || location.pathname.startsWith(`${item.url}/`)
                      }
                      tooltip={item.title}
                      className="h-11 gap-3 rounded-lg px-3 text-[14px] font-normal text-sidebar-foreground/65 hover:bg-background hover:text-sidebar-foreground data-[active=true]:bg-background data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:[&>span]:hidden [&>svg]:size-[18px]"
                      data-tour={item.url === "/compliance" ? "compliance-menu" : undefined}
                    >
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>
      </SidebarContent>
      {showBanner && (
        <div className="px-2 pb-3 group-data-[collapsible=icon]:hidden">
          <RequestAccessBanner />
        </div>
      )}
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  disabled={isLoading}
                >
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                    <User className="size-4" />
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-semibold">{userName}</span>
                    <span className="truncate text-xs text-muted-foreground">{userEmail}</span>
                  </div>
                  <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" className="w-[--radix-popper-anchor-width] mb-2">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/billing" className="cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>Billing</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer" data-tour="settings-integrations">
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
